import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Grid3x3 } from "lucide-react";

function cellColor(pct) {
  if (pct >= 50) return "bg-emerald-500/30 text-emerald-300";
  if (pct >= 25) return "bg-emerald-500/15 text-emerald-400/80";
  if (pct >= 10) return "bg-amber-500/15 text-amber-400/80";
  if (pct > 0) return "bg-red-500/15 text-red-400/80";
  return "bg-white/[0.02] text-white/15";
}

export default function RetentionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAxios.get("/analytics/command/retention?weeks=12")
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-white/20">Loading retention data...</div>;

  const cohorts = data?.cohorts || [];
  const maxWeeks = data?.weeks || 12;

  return (
    <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="retention-cohort">
      <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
        <Grid3x3 className="w-4 h-4" /> Weekly Retention Cohorts
      </h2>
      <p className="text-xs text-white/30 mb-4">
        Rows = weekly cohorts (most recent first). Columns = weeks since first visit. Cell = % of users returning.
      </p>

      {cohorts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="text-[11px] border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 px-2 text-white/30 font-medium sticky left-0 bg-[#111113] z-10 min-w-[80px]">Cohort</th>
                <th className="py-2 px-2 text-white/30 font-medium text-center min-w-[40px]">Size</th>
                {Array.from({ length: Math.min(maxWeeks, 12) + 1 }).map((_, i) => (
                  <th key={i} className="py-2 px-2 text-white/30 font-medium text-center min-w-[44px]">
                    W{i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort, ci) => (
                <tr key={cohort.week} data-testid={`cohort-row-${ci}`}>
                  <td className="py-1.5 px-2 text-white/50 font-mono sticky left-0 bg-[#111113] z-10 border-b border-white/[0.03]">
                    {cohort.week}
                  </td>
                  <td className="py-1.5 px-2 text-white/60 font-mono text-center border-b border-white/[0.03]">
                    {cohort.size}
                  </td>
                  {Array.from({ length: Math.min(maxWeeks, 12) + 1 }).map((_, wi) => {
                    const pct = cohort.retention[wi] ?? 0;
                    // Don't show cells beyond what this cohort can have
                    const cohortAge = cohorts.length - ci;
                    const canShow = wi <= cohortAge;
                    return (
                      <td key={wi} className="py-1.5 px-1 text-center border-b border-white/[0.03]">
                        {canShow && pct > 0 ? (
                          <span className={`inline-block w-full rounded px-1 py-0.5 font-mono ${cellColor(pct)}`}>
                            {pct}%
                          </span>
                        ) : canShow ? (
                          <span className="text-white/10 font-mono">0%</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-white/20 text-sm text-center py-12">Not enough data for cohort analysis yet</div>
      )}

      <div className="flex items-center gap-4 mt-4 text-[10px] text-white/30">
        <span>Legend:</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/15" /> &lt;10%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/15" /> 10-25%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/15" /> 25-50%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/30" /> &gt;50%</span>
      </div>
    </section>
  );
}
